import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";
import logoIcon from "../../assets/common/logo-icon.svg";
import logout from "../../assets/common/logout.svg";
import failed from "../../assets/dashboard/failed-icon.svg";
import AdminSidebar from "components/common/AdminSidebar";
import { useTour } from "@reactour/tour";
import { getUploadTourSteps } from "./adminTourSteps";

function DataConfirmation() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [uploadData, setUploadData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  const clientName = sessionStorage.getItem("clientName");
  const adminTourSteps = React.useMemo(() => getUploadTourSteps(t), [t]);
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    const saved = localStorage.getItem("adminSidebarPinned");
    return saved === "true";
  });

  const { setIsOpen } = useTour();

  React.useEffect(() => {
    const role = sessionStorage.getItem("userRole");

    const hasSeen = localStorage.getItem("kikonnect_upload_tour_shown");

    if (role !== "Admin" || hasSeen) return;

    let canceled = false; // The adminTourSteps constant is now defined above via useMemo

    const requiredSelectors = adminTourSteps
      .map((step) => step.selector)
      .filter(Boolean);

    const allAnchorsPresent = () =>
      requiredSelectors.every((sel) => {
        try {
          return !!document.querySelector(sel);
        } catch {
          return false;
        }
      });

    const start = Date.now();

    const timeoutMs = 2000; // wait up to 4s for slower devices

    const tryOpen = () => {
      if (canceled) return;

      if (allAnchorsPresent() || Date.now() - start > timeoutMs) {
        setIsOpen(true);

        localStorage.setItem("kikonnect_upload_tour_shown", "1");

        return;
      } // re-check on next frame

      requestAnimationFrame(tryOpen);
    }; // kick off after initial paint

    const raf = requestAnimationFrame(tryOpen);

    return () => {
      canceled = true;

      cancelAnimationFrame(raf);
    };
  }, [setIsOpen, adminTourSteps]);

  useEffect(() => {
    const storedData = sessionStorage.getItem("uploadResults");
    if (storedData) {
      setUploadData(JSON.parse(storedData));
    }
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  const downloadErrorCSV = () => {
    if (!uploadData?.errorRecords?.length) {
      alert("No error records to download.");
      return;
    }

    const orderedHeaders = [
      "ClientId",
      "FirstName",
      "LastName",
      "Role",
      "Company",
      "Industry",
      "Email",
      "AltEmail",
      "Phone",
      "Mobile",
      "City",
      "Country",
      "Website",
      "Source",
    ];

    // Ensure headers are also handled correctly if they contain special characters
    const csvHeaders = orderedHeaders.map(header =>
      typeof header === "string" && header.includes(",")
        ? `"${header.replace(/"/g, '""')}"`
        : header
    ).join(",");

    const csvRows = uploadData.errorRecords.map((record) => {
      const dataValues = orderedHeaders.map((header) => {
        let value = record.data[header];
        // Ensure all string values are properly encoded for CSV
        if (typeof value === "string") {
          // Escape double quotes by doubling them
          value = value.replace(/"/g, '""');
          // Enclose in double quotes if the value contains commas, newlines, or double quotes
          if (value.includes(",") || value.includes("\n") || value.includes('"')) {
            value = `"${value}"`;
          }
        } else if (value === null || value === undefined) {
          value = ''; // Treat null/undefined as empty string
        }
        return value;
      });

      const errorMessages = Object.values(record.errors)
        .map((error) => {
          // Ensure error messages are also properly encoded
          if (typeof error === "string") {
            error = error.replace(/"/g, '""');
            if (error.includes(",") || error.includes("\n") || error.includes('"')) {
              error = `"${error}"`;
            }
          } else if (error === null || error === undefined) {
            error = '';
          }
          return error;
        })
        .join("; "); // Using semicolon as a separator for multiple errors within a single field

      return [...dataValues, errorMessages].join(",");
    });

    // Add "Error Reason" header and its corresponding data
    const finalCsvHeaders = `${csvHeaders},Error Reason`;
    const finalCsvRows = csvRows.map(row => `${row},"${uploadData.errorRecords.find(rec => rec.data.Email === row.split(',')[5])?.errors ? Object.values(uploadData.errorRecords.find(rec => rec.data.Email === row.split(',')[5]).errors).map(err => typeof err === 'string' ? err.replace(/"/g, '""') : '').join('; ') : ''}"`);

    // Reconstruct the CSV content for download
    const csvContent = [finalCsvHeaders, ...csvRows].join("\n");

    // Use BOM for UTF-8 to help Excel recognize the encoding
    const bom = "\uFEFF";
    const blob = new Blob([bom + csvContent], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "error_records.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const totalPages = Math.ceil(uploadData?.allRecords?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentRecords = uploadData?.allRecords?.slice(startIndex, endIndex);

  return (
    <div className="flex min-h-screen bg-secondaryBg">
      <AdminSidebar
        logoIcon={logoIcon}
        logout={logout}
        labelClassName="bg-primary"
        onToggle={(pinned) => setSidebarPinned(pinned)}
      />
      <div className={`flex-1 flex flex-col px-4 sm:px-8 py-6 md:py-10 mt-10 transition-all duration-300 ${sidebarPinned ? "ml-[250px]" : "ml-[80px]"
        }`}>
        <div className="max-w-6xl mx-auto w-full">
          <h1 className="text-[30px] font-bold text-primary mb-2">
            {t("confirm.head")}
          </h1>
          <p className="mb-2 md:text-xl text-primary">
            {t("confirm.description")}{" "}
            <span className="font-bold">{clientName}</span>.
          </p>
          <div className="relative flex items-center mb-8">
            <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
            <hr className="flex-1 border-t-2 border-[#0A1E4C]" />
            <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
          </div>
          {!uploadData ? (
            <>
              <div className="flex items-center justify-center my-24">
                <p className="text-xl text-primary font-semibold">
                  {t("confirm.message")}
                </p>
              </div>
              <button
                type="button"
                className="text-primary flex text-xl sm:text-2xl font-semibold"
                onClick={handleBack}
              >
                {t("back")}
              </button>
            </>
          ) : (
            <>
              <div className="justify-between flex">
                <p>
                  {uploadData.uploaded}/{uploadData.total}{" "}
                  {t("dashboard.uploadContact.recordsUploaded")}
                </p>
                <Button
                  variant="primary"
                  onClick={downloadErrorCSV}
                  className="w-40"
                  data-tour-id="download-error"
                >
                  {t("download")}
                </Button>
              </div>
              <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 mt-10">
                <div className="bg-white rounded-xl shadow-lg p-0 w-[350px] md:w-full h-auto border border-[#E3E8F0] overflow-x-auto overflow-y-hidden">
                  <table className="w-full text-primary">
                    <thead>
                      <tr className="bg-buttonFalse text-primary ">
                        <th className="p-4 border-b-2">
                          {t("confirm.table.id")}
                        </th>
                        <th className="p-4 border-b-2">
                          {t("confirm.table.name")}
                        </th>
                        <th className="p-4 border-b-2">
                          {t("confirm.table.lastName")}
                        </th>
                        <th className="p-4 border-b-2">
                          {t("dashboard.table.company")}
                        </th>
                        <th className="p-4 border-b-2">
                          {t("confirm.table.industry")}
                        </th>
                        <th className="p-4 border-b-2">{t("email")}</th>
                        <th className="p-4 border-b-2">
                          {t("confirm.table.alt")}
                        </th>
                        <th className="p-4 border-b-2">{t("admin.phone")}</th>
                        <th className="p-4 border-b-2">
                          {t("confirm.table.mobile")}
                        </th>
                        <th className="p-4 border-b-2">
                          {t("confirm.table.city")}
                        </th>
                        <th className="p-4 border-b-2">
                          {t("confirm.table.country")}
                        </th>
                        <th className="p-4 border-b-2">
                          {t("confirm.table.website")}
                        </th>
                        <th className="p-4 border-b-2">
                          {t("confirm.table.source")}
                        </th>

                      </tr>
                    </thead>
                    <tbody>
                      {currentRecords
                        ?.filter((record) =>
                          Object.values(record).some(
                            (value) => value && value.trim()
                          )
                        )
                        .map((record, idx) => {
                          const isError = uploadData.errorRecords.some(
                            (errorRecord) =>
                              errorRecord.data.Email === record.Email &&
                              errorRecord.data.FirstName === record.FirstName &&
                              errorRecord.data.LastName === record.LastName
                          );
                          return (
                            <tr
                              key={idx}
                              className={`${isError ? "bg-red-100" : "hover:bg-gray-50"
                                } text-center relative`}
                            >
                              {isError ? (
                                <td className="p-4 border-b" data-tour-id="error-upload">
                                  <img
                                    src={failed}
                                    alt="Error"
                                    className="w-8 h-8 object-cover flex items-center justify-center"
                                  />
                                </td>
                              ) : (
                                <td className="p-4 border-b">
                                  {record.ClientId}
                                </td>
                              )}
                              <td className="p-4 border-b">
                                {record.FirstName}
                              </td>
                              <td className="p-4 border-b">
                                {record.LastName}
                              </td>
                              <td className="p-4 border-b">{record.Company}</td>
                              <td className="p-4 border-b">{record.Industry}</td>
                              <td className="p-4 border-b">{record.Email}</td>
                              <td className="p-4 border-b">
                                {record.AltEmail}
                              </td>
                              <td className="p-4 border-b">{record.Phone}</td>
                              <td className="p-4 border-b">{record.Mobile}</td>
                              <td className="p-4 border-b">{record.City}</td>
                              <td className="p-4 border-b">{record.Country}</td>
                              <td className="p-4 border-b">{record.Website}</td>
                              <td className="p-4 border-b">{record.Source}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="flex items-center justify-center space-x-5 text-sm my-5">
                <button
                  className={`p-2 rounded border-tone ${currentPage === 1
                    ? "bg-gray-100 text-gray-400"
                    : "bg-white text-icon"
                    }`}
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                    className="w-4 h-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <span className="text-[#000] font-semibold">
                  <strong className="bg-white p-1 rounded border-tone">
                    {currentPage}
                  </strong>{" "}
                  of {totalPages}
                </span>
                <button
                  className={`p-2 rounded border-tone ${currentPage === totalPages
                    ? "bg-gray-100 text-gray-400"
                    : "bg-white text-icon"
                    }`}
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                    className="w-4 h-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>

              <div className="md:col-span-2 flex justify-between items-center mt-4">
                <button
                  type="button"
                  className="text-primary flex text-xl sm:text-2xl font-semibold"
                  onClick={handleBack}
                >
                  {t("back")}
                </button>
                <Link to="/dashboard">
                  <Button variant="primary" className="w-40">
                    {t("continue")}
                  </Button>
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default DataConfirmation;
